import { AppSidebar } from "./AppSidebar";
import { FavoritesDropdown } from "./FavoritesBar";
import { EmailNav } from "./EmailNav";
import { AccountsNav } from "./AccountsNav";
import { ProjectsNav } from "./ProjectsNav";
import { Bell, CircleHelp, Clock, ClipboardCheck, GitBranch, AtSign, Lightbulb, Play, Search, Users, Check, CheckCircle, MessageSquare, MessageCircle, Upload, CheckSquare, Settings, Plus, ChevronDown, ArrowRight, Trash2, X, Plug, Unlock, Mail, Landmark } from "lucide-react";
type RightSidebarTab = "integrations" | "help";
import { useEffect, useState, useRef } from "react";
import { useTimerContext } from "@/lib/timerContext";
import { formatMinutesShort } from "@/lib/timerHold";
import { TimerSheet } from "@/components/custom/TimerSheet";
import { HelpSheet } from "@/components/custom/HelpSheet";
import AIChatBubble from "@/components/custom/AIChatBubble";
import { EmailSearchOverlay } from "@/components/custom/EmailSearchOverlay";
import { RightSidebar } from "@/components/layout/RightSidebar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { SearchPanel, type SearchPanelHandle } from "@/components/custom/SearchPanel";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { useQuickCreate } from "@/lib/quickCreateContext";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { getNotifications, markNotificationRead, markNotificationUnread, markAllNotificationsRead, deleteNotification, notificationTargetUrl, type AppNotification } from "@/lib/api";
import { formatDistanceToNow } from "date-fns";
import { useLocation } from "wouter";

interface LayoutProps {
  children: React.ReactNode;
  headerContent?: React.ReactNode;
}

function NotificationBell() {
  const queryClient = useQueryClient();
  const [, navigate] = useLocation();
  const [open, setOpen] = useState(false);
  const [showUnreadOnly, setShowUnreadOnly] = useState(true);

  const { data } = useQuery({
    queryKey: ["notifications"],
    queryFn: getNotifications,
    refetchInterval: 30_000,
    staleTime: 15_000,
  });

  const notifications = data?.notifications ?? [];
  const unreadCount = data?.unread_count ?? 0;

  const markReadMutation = useMutation({
    mutationFn: markNotificationRead,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["notifications"] }),
  });

  const markUnreadMutation = useMutation({
    mutationFn: markNotificationUnread,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["notifications"] }),
  });

  const markAllMutation = useMutation({
    mutationFn: markAllNotificationsRead,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["notifications"] }),
  });

  const deleteMutation = useMutation({
    mutationFn: deleteNotification,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["notifications"] }),
  });

  const handleClick = (n: AppNotification) => {
    if (!n.is_read) markReadMutation.mutate(n.id);
    setOpen(false);
    const url = notificationTargetUrl(n);
    if (url) navigate(url);
  };

  const notifIcon = (type: AppNotification["type"]) => {
    if (type === "task_assigned") return <ClipboardCheck className="w-4 h-4 text-blue-500 shrink-0 mt-0.5" />;
    if (type === "task_comment") return <MessageSquare className="w-4 h-4 text-blue-400 shrink-0 mt-0.5" />;
    if (type === "mention") return <AtSign className="w-4 h-4 text-violet-500 shrink-0 mt-0.5" />;
    if (type === "stage_changed" || type === "status_changed") return <GitBranch className="w-4 h-4 text-amber-500 shrink-0 mt-0.5" />;
    if (type === "project_assigned") return <Users className="w-4 h-4 text-green-500 shrink-0 mt-0.5" />;
    if (type === "project_completed") return <CheckCircle className="w-4 h-4 text-emerald-500 shrink-0 mt-0.5" />;
    if (type === "project_comment") return <MessageSquare className="w-4 h-4 text-sky-500 shrink-0 mt-0.5" />;
    if (type === "client_message") return <MessageCircle className="w-4 h-4 text-teal-500 shrink-0 mt-0.5" />;
    if (type === "client_document_upload") return <Upload className="w-4 h-4 text-orange-500 shrink-0 mt-0.5" />;
    if (type === "client_task_complete") return <CheckSquare className="w-4 h-4 text-emerald-400 shrink-0 mt-0.5" />;
    if (type === "client_task_comment") return <MessageSquare className="w-4 h-4 text-teal-500 shrink-0 mt-0.5" />;
    if (type === "task_unblocked") return <Unlock className="w-4 h-4 text-green-500 shrink-0 mt-0.5" />;
    if (type === "email_assigned") return <Mail className="w-4 h-4 text-indigo-500 shrink-0 mt-0.5" />;
    if (type === "feedback_resolved") return <CheckSquare className="w-4 h-4 text-emerald-500 shrink-0 mt-0.5" />;
    if (type === "document_delete_requested" || type === "document_deleted" || type === "document_delete_decided") return <Trash2 className="w-4 h-4 text-amber-600 shrink-0 mt-0.5" />;
    return <Bell className="w-4 h-4 text-muted-foreground shrink-0 mt-0.5" />;
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          className="relative p-2 rounded-full hover:bg-accent hover:text-accent-foreground transition-colors text-muted-foreground"
          data-testid="button-notifications"
          title="Notifications"
        >
          <Bell className="w-5 h-5" />
          {unreadCount > 0 && (
            <span className="absolute top-1 right-1 min-w-[16px] h-4 px-0.5 bg-destructive text-destructive-foreground rounded-full text-[10px] font-bold flex items-center justify-center border border-card">
              {unreadCount > 9 ? "9+" : unreadCount}
            </span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-[30rem] max-w-[calc(100vw-2rem)] p-0" align="end">
        <div className="flex items-center justify-between px-4 py-3 border-b">
          <span className="font-semibold text-sm">Notifications</span>
          <div className="flex items-center gap-3">
            <button
              onClick={() => setShowUnreadOnly((v) => !v)}
              title={showUnreadOnly ? "Showing unread only — click to show all" : "Showing all — click to show unread only"}
              className={`text-xs px-2 py-1 rounded-full border transition-colors ${
                showUnreadOnly
                  ? "bg-primary text-primary-foreground border-primary font-semibold hover:bg-primary/90"
                  : "bg-transparent text-muted-foreground border-border hover:text-foreground hover:bg-muted"
              }`}
            >
              {showUnreadOnly ? "Unread Only" : "All"}
            </button>
            <button
              onClick={() => { setOpen(false); navigate("/notifications"); }}
              className="p-1 rounded-full text-primary hover:text-primary/80 hover:bg-muted transition-colors"
              title="View all notifications"
              aria-label="View all notifications"
            >
              <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        </div>
        {/* show strip only when there are unread */}
        {unreadCount > 0 && (
          <div className="flex justify-end px-4 py-1.5 border-b bg-muted/20">
            <button
              onClick={() => markAllMutation.mutate()}
              disabled={markAllMutation.isPending}
              className="text-xs text-muted-foreground hover:text-foreground transition-colors disabled:opacity-50"
            >
              Mark All Read
            </button>
          </div>
        )}
        <div className="max-h-96 overflow-y-auto">
          {(() => {
            const filtered = showUnreadOnly ? notifications.filter((n) => !n.is_read) : notifications;
            if (filtered.length === 0) return (
              <div className="py-8 text-center text-sm text-muted-foreground">
                {showUnreadOnly ? "No unread notifications" : "No notifications yet"}
              </div>
            );
            return filtered.slice(0, 10).map((n) => (
              <div
                key={n.id}
                className={`w-full flex gap-3 px-4 py-3 hover:bg-muted transition-colors border-b last:border-0 ${!n.is_read ? "bg-primary/5 border-l-2 border-l-primary" : ""}`}
              >
                {/* read/unread toggle */}
                <button
                  onClick={() => (n.is_read ? markUnreadMutation : markReadMutation).mutate(n.id)}
                  title={n.is_read ? "Mark as unread" : "Mark as read"}
                  aria-label={n.is_read ? "Mark as unread" : "Mark as read"}
                  className={`shrink-0 mt-0.5 w-5 h-5 rounded-full border-2 flex items-center justify-center transition-colors ${
                    n.is_read
                      ? "bg-green-500 border-green-500 hover:bg-green-400 hover:border-green-400"
                      : "border-muted-foreground/40 hover:border-green-400 bg-transparent"
                  }`}
                >
                  {n.is_read && <Check className="w-3 h-3 text-white" strokeWidth={3} />}
                </button>
                <button
                  onClick={() => handleClick(n)}
                  className="flex-1 min-w-0 flex gap-3 text-left"
                >
                  <div className="mt-0.5 shrink-0">{notifIcon(n.type)}</div>
                  <div className="flex-1 min-w-0">
                    {n.account_name && (
                      <p className="text-[10px] font-semibold uppercase tracking-wide text-primary/70 truncate">{n.account_name}</p>
                    )}
                    <p className={`text-sm truncate ${!n.is_read ? "font-semibold" : ""}`}>{n.title}</p>
                    {n.body && <p className="text-xs text-muted-foreground truncate">{n.body}</p>}
                    <p className="text-xs text-muted-foreground/60 mt-0.5">
                      {formatDistanceToNow(new Date(n.created_at), { addSuffix: true })}
                    </p>
                  </div>
                </button>
                <button
                  onClick={() => deleteMutation.mutate(n.id)}
                  disabled={deleteMutation.isPending && deleteMutation.variables === n.id}
                  title="Delete permanently"
                  aria-label="Delete notification"
                  className="shrink-0 mt-0.5 p-1.5 rounded-md text-muted-foreground hover:bg-destructive/10 hover:text-destructive transition-colors disabled:opacity-40"
                >
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </div>
            ));
          })()}
        </div>
        {/* Footer: Settings link */}
        <div className="flex items-center justify-between px-4 py-2 border-t bg-muted/30">
          <button
            onClick={() => { setOpen(false); navigate("/settings"); }}
            className="text-xs text-muted-foreground hover:text-foreground flex items-center gap-1 transition-colors"
          >
            <Settings className="w-3 h-3" />
            Settings
          </button>
        </div>
      </PopoverContent>
    </Popover>
  );
}

export function Layout({ children, headerContent }: LayoutProps) {
  const { timerRunning, timerPaused, timerStopped, heldTimer, duration, elapsedSeconds, formatTimer, openSheet, handleStartTimer } = useTimerContext();
  const { setTrigger } = useQuickCreate();
  const [, navigate] = useLocation();

  const [rightOpen, setRightOpen] = useState(false);
  const [rightTab, setRightTab] = useState<RightSidebarTab>("integrations");
  const [createDropdownOpen, setCreateDropdownOpen] = useState(false);
  const pendingLogTime = useRef(false);

  // Open the Log Time sheet only after the dropdown exit animation completes (~150ms)
  useEffect(() => {
    if (!createDropdownOpen && pendingLogTime.current) {
      pendingLogTime.current = false;
      const t = setTimeout(() => openSheet(), 500);
      return () => clearTimeout(t);
    }
  }, [createDropdownOpen]);

  function openRight(tab: RightSidebarTab) {
    setRightTab(tab);
    setRightOpen(true);
  }
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const searchInputRef = useRef<HTMLInputElement>(null);
  const searchContainerRef = useRef<HTMLDivElement>(null);
  const searchPanelRef = useRef<SearchPanelHandle>(null);

  const openSearch = () => {
    setSearchOpen(true);
    setTimeout(() => searchInputRef.current?.focus(), 20);
  };

  const closeSearch = () => {
    setSearchOpen(false);
    setSearchQuery("");
  };

  // Ctrl+K / Cmd+K focuses the search input
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key === "k") {
        e.preventDefault();
        openSearch();
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, []);

  // Close on outside click
  useEffect(() => {
    if (!searchOpen) return;
    const handler = (e: MouseEvent) => {
      if (searchContainerRef.current && !searchContainerRef.current.contains(e.target as Node)) {
        closeSearch();
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [searchOpen]);

  return (
    <div className="flex h-screen w-full bg-background overflow-hidden print:block print:h-auto print:overflow-visible">
      <div className="print:hidden"><AppSidebar /></div>
      <main className="flex-1 flex flex-col h-full overflow-hidden relative z-0 print:block print:h-auto print:overflow-visible">
        <header className="min-h-16 py-2 border-b border-border bg-card/50 backdrop-blur-sm px-6 flex items-center justify-between shrink-0 z-10 print:hidden">
          <div className="flex items-center gap-4 w-1/3">
            <div ref={searchContainerRef} className="relative w-full max-w-xs">
              {/* Search bar — the only input */}
              <div
                className={`flex items-center gap-2 h-9 pl-3 pr-3 rounded-full border transition-all text-sm cursor-text ${
                  searchOpen
                    ? "bg-background border-border shadow-sm ring-1 ring-primary/20"
                    : "bg-muted/50 border-transparent hover:bg-muted hover:border-border text-muted-foreground"
                }`}
                onClick={openSearch}
                data-testid="button-search"
              >
                <Search className="w-4 h-4 shrink-0 text-muted-foreground" />
                <input
                  ref={searchInputRef}
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onFocus={() => setSearchOpen(true)}
                  onKeyDown={(e) => searchPanelRef.current?.handleKeyDown(e)}
                  placeholder="Search…"
                  className="flex-1 bg-transparent outline-none text-sm placeholder:text-muted-foreground"
                />
                {searchOpen && searchQuery ? (
                  <button
                    onMouseDown={(e) => { e.preventDefault(); setSearchQuery(""); searchInputRef.current?.focus(); }}
                    className="text-muted-foreground hover:text-foreground transition-colors"
                  >
                    <X className="w-3.5 h-3.5" />
                  </button>
                ) : !searchOpen ? (
                  <kbd className="hidden sm:inline-flex items-center gap-0.5 text-xs text-muted-foreground/60 font-mono pointer-events-none">
                    <span>Ctrl</span><span>K</span>
                  </kbd>
                ) : null}
              </div>

              {/* Dropdown — appears below the search bar */}
              {searchOpen && (
                <div className="absolute top-full left-0 mt-2 z-50">
                  <SearchPanel ref={searchPanelRef} query={searchQuery} onClose={closeSearch} />
                </div>
              )}
            </div>
          </div>

          {headerContent && (
            <div className="flex items-center justify-center flex-1">
              {headerContent}
            </div>
          )}

          <div className="flex items-center justify-end gap-2 flex-wrap">
            <AccountsNav />
            <ProjectsNav />
            <EmailNav />

            <a
              href="https://carpco-os.ccpas.net/clients"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-1.5 px-3 h-9 rounded-full bg-primary hover:bg-primary/90 text-primary-foreground text-sm font-medium transition-colors"
              title="Open Carpco OS"
              data-testid="link-carpco-os"
            >
              <Landmark className="w-3.5 h-3.5" />
              <span className="hidden sm:inline">Carpco OS</span>
            </a>

            <FavoritesDropdown />

            {/* Quick-start timer button — only visible when no timer is running, paused or
                waiting to be saved (starting over a stopped one would lose its time) */}
            {!timerRunning && !timerPaused && !timerStopped && (
              <button
                onClick={() => { handleStartTimer(); openSheet(); }}
                className="flex items-center justify-center h-8 w-8 rounded-full hover:bg-accent hover:text-accent-foreground transition-colors text-muted-foreground"
                title="Start timer"
                data-testid="button-quick-start-timer"
              >
                <Play className="w-4 h-4 fill-current" />
              </button>
            )}

            {/* Clock button — shows elapsed time when running */}
            <button
              onClick={() => openSheet()}
              className={`flex items-center gap-1.5 px-3 h-9 rounded-full text-sm font-medium transition-colors ${
                timerRunning
                  ? "bg-primary/10 text-primary border border-primary/30 hover:bg-primary/15"
                  : timerPaused || timerStopped
                  ? "bg-amber-50 text-amber-600 border border-amber-200 hover:bg-amber-100"
                  : "hover:bg-accent hover:text-accent-foreground text-muted-foreground"
              }`}
              title={timerStopped ? "Stopped timer: open to save or discard" : "Log time"}
              data-testid="button-timer"
            >
              {timerRunning ? (
                <>
                  <span className="relative flex h-2 w-2">
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-primary opacity-75"></span>
                    <span className="relative inline-flex rounded-full h-2 w-2 bg-primary"></span>
                  </span>
                  <span className="font-mono tabular-nums">{formatTimer(elapsedSeconds)}</span>
                </>
              ) : timerPaused ? (
                <>
                  <Clock className="w-4 h-4" />
                  <span className="font-mono tabular-nums">{formatTimer(elapsedSeconds)}</span>
                </>
              ) : timerStopped ? (
                <>
                  <Clock className="w-4 h-4" />
                  <span className="tabular-nums" data-testid="timer-unsaved-chip">{formatMinutesShort(duration * 60)} unsaved</span>
                </>
              ) : (
                <Clock className="w-4 h-4" />
              )}
              {heldTimer && (
                <span
                  className="ml-0.5 rounded-full bg-amber-100 px-1.5 text-[10px] font-semibold text-amber-700 dark:bg-amber-900/60 dark:text-amber-200"
                  title="A timer is on hold"
                  data-testid="timer-on-hold-chip"
                >
                  +1 on hold
                </span>
              )}
            </button>

            {/* Quick-Create */}
            <DropdownMenu open={createDropdownOpen} onOpenChange={setCreateDropdownOpen}>
              <DropdownMenuTrigger asChild>
                <button
                  className="flex items-center gap-1 px-3 h-9 rounded-full bg-primary hover:bg-primary/90 text-primary-foreground text-sm font-medium transition-colors"
                  data-testid="button-quick-create"
                >
                  <Plus className="w-4 h-4" />
                  Create
                  <ChevronDown className="w-3.5 h-3.5 opacity-70" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-44">
                <DropdownMenuItem onClick={() => { navigate("/accounts"); setTrigger("new-client"); }}>
                  New Client
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => { navigate("/"); setTrigger("new-project"); }}>
                  New Project
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => { navigate("/templates"); setTrigger("new-template"); }}>
                  New Template
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onSelect={(e) => {
                  e.preventDefault();
                  pendingLogTime.current = true;
                  setCreateDropdownOpen(false);
                }}>
                  Log Time
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>

            {/* Feedback */}
            <button
              onClick={() => navigate("/feedback")}
              className="p-2 rounded-full hover:bg-accent hover:text-accent-foreground transition-colors text-muted-foreground"
              title="Feedback & changelog"
              data-testid="button-feedback"
            >
              <Lightbulb className="w-5 h-5" />
            </button>

            {/* Notifications */}
            <NotificationBell />

            {/* Integrations + Help panel trigger — far right */}
            <button
              onClick={() => rightOpen ? setRightOpen(false) : openRight("integrations")}
              className={`p-2 rounded-full transition-colors ${
                rightOpen
                  ? "bg-primary/10 text-primary"
                  : "hover:bg-accent hover:text-accent-foreground text-muted-foreground"
              }`}
              title="Integrations &amp; Help"
              data-testid="button-right-panel"
            >
              <Plug className="w-5 h-5" />
            </button>
          </div>
        </header>

        <div id="layout-scroll" className="flex-1 overflow-auto p-4 md:p-6 scroll-smooth print:overflow-visible print:p-0">
          <div className="w-full space-y-6 animate-in fade-in duration-500 slide-in-from-bottom-2 print:animate-none">
            {children}
          </div>
        </div>
      </main>

      <div className="print:hidden">
        <RightSidebar open={rightOpen} defaultTab={rightTab} onOpenChange={setRightOpen} />
        <TimerSheet />
        <AIChatBubble />
        <EmailSearchOverlay />
      </div>
    </div>
  );
}
